import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";

import { GlowCard, type GlowCardProps } from "./ui/glow-card.js";
import { cn } from "../lib/utils.js";
import { MenuList, MENU_ITEM_ATTR } from "./ui/menu-list.js";
import { SpecularButton, type SpecularButtonProps } from "./ui/specular-button.js";
import { Button } from "./ui/button.js";
import { CheckboxList } from "./ui/checkbox-list.js";

// Closes an open dropdown/popover when the user clicks anywhere outside `ref`.
// Shared so every dropdown on a page can be coordinated by one "which is open" state —
// opening one automatically closes any other, since each just gets `active: false`.
export function useCloseOnOutsideClick(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  onClose: () => void,
  /** A second element that also counts as "inside" — a panel rendered through
   *  a portal is no longer a DOM descendant of the trigger's container, and
   *  without this a click on an option would close the menu on mousedown and
   *  the option would never receive its click. */
  alsoInside?: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!active) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (alsoInside?.current?.contains(t)) return;
      onClose();
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [active, onClose, ref, alsoInside]);
}

/** Gap between the trigger and the panel, px — matches the `mt-1` of the
 *  in-flow variant so both read the same. */
const ANCHOR_GAP = 4;
/** Below less than this much free space, the panel opens upward instead. */
const FLIP_THRESHOLD = 200;

interface AnchorBox { style: CSSProperties; placement: "below" | "above" }

/**
 * Positions a portalled panel against its trigger using viewport coordinates,
 * re-measuring on scroll (capture, so inner scroll containers count) and on
 * resize. The values are DOM measurements recomputed as the page moves, so
 * they are passed as CSS custom properties that `.dropdown-panel-anchored`
 * in tokens.css reads (CLAUDE.md's runtime-measurement exception).
 */
function useAnchorBox(anchor: RefObject<HTMLElement | null>, open: boolean, align: "start" | "end"): AnchorBox | null {
  const [box, setBox] = useState<AnchorBox | null>(null);

  useLayoutEffect(() => {
    if (!open) { setBox(null); return; }
    const place = () => {
      const el = anchor.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - ANCHOR_GAP;
      const above = r.top - ANCHOR_GAP;
      const flip = below < FLIP_THRESHOLD && above > below;
      setBox({
        placement: flip ? "above" : "below",
        style: {
          "--dp-top": flip ? "auto" : `${r.bottom + ANCHOR_GAP}px`,
          "--dp-bottom": flip ? `${window.innerHeight - r.top + ANCHOR_GAP}px` : "auto",
          "--dp-left": align === "end" ? "auto" : `${r.left}px`,
          "--dp-right": align === "end" ? `${window.innerWidth - r.right}px` : "auto",
          "--dp-min-width": `${r.width}px`,
          "--dp-max-height": `${Math.max(120, (flip ? above : below) - 8)}px`,
        } as CSSProperties,
      });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, align, anchor]);

  return box;
}

// Lets the press pulse and check-mark pop play before a single-select closes.
const CLOSE_DELAY_MS = 160;

interface DropdownButtonProps extends Pick<SpecularButtonProps, "disabled" | "className" | "title"> {
  expanded: boolean;
  onClick: () => void;
  "aria-label"?: string | undefined;
  children: ReactNode;
}

// The trigger of every dropdown: a SpecularButton (highlight travels its border
// toward the pointer) with a chevron that rotates to reflect the expanded state.
export function DropdownButton({ expanded, onClick, className, children, ...props }: DropdownButtonProps) {
  return (
    <SpecularButton
      size="lg"
      aria-expanded={expanded}
      aria-haspopup="listbox"
      onClick={onClick}
      className={cn("h-9 gap-1 px-2.5 font-normal text-surface-900", expanded && "bg-surface-50", className)}
      {...props}
    >
      {children}
      <ChevronDown size={14} className={cn("transition-transform duration-200", expanded && "rotate-180")} />
    </SpecularButton>
  );
}

// The floating panel every dropdown/popover opens into. Mounts with opacity 0 and a
// slight upward offset, then animates to its resting state on the next frame — reused
// by DropdownSelect's option list and any other popover (Columns picker, notifications)
// so every dropdown opens with the same smooth transition. Size / scroll it via className.
export interface DropdownPanelProps {
  children: ReactNode;
  className?: string;
  align?: "start" | "end";
  /**
   * Renders the panel on a GlowCard instead of a plain surface, so a dropdown
   * that reads as a card (the top-bar attendance and notification panels) gets
   * the same edge light as the cards on the page. Omit for ordinary menus.
   */
  glow?: GlowCardProps["glow"];
  /**
   * Anchor the panel to this element and render it on <body> through a portal,
   * instead of positioning it inside the trigger's own container. Pass it
   * whenever the trigger can sit inside a modal, a table wrapper or any other
   * element with a non-visible `overflow`, which would clip an absolutely
   * positioned panel. It also flips the panel above the trigger near the
   * bottom of the viewport. DropdownSelect always passes it.
   */
  anchorRef?: RefObject<HTMLElement>;
  /** Needed with `anchorRef`: the portalled panel is outside the trigger's
   *  container, so the outside-click check has to be told about it. */
  panelRef?: RefObject<HTMLDivElement>;
  /**
   * In-flow (non-anchored) panels: closes on a mousedown outside the trigger's
   * container (the panel's parent) and on Escape. Anchored panels are closed by
   * their owner through `useCloseOnOutsideClick`.
   */
  onClose?: () => void;
}

export function DropdownPanel({ children, className, align = "start", glow, anchorRef, panelRef, onClose }: DropdownPanelProps) {
  const [visible, setVisible] = useState(false);
  const fallbackRef = useRef<HTMLDivElement>(null);
  const box = useAnchorBox(anchorRef ?? fallbackRef, !!anchorRef, align);

  useEffect(() => {
    if (!onClose || anchorRef) return;
    const onDown = (e: MouseEvent) => {
      const container = (panelRef ?? fallbackRef).current?.parentElement;
      if (container && !container.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, anchorRef, panelRef]);

  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Positioning and the open transition are identical either way; only the
  // surface differs. GlowCard supplies its own border, background and radius,
  // so those are left off the glow variant's class list.
  const anchored = !!anchorRef;
  const above = box?.placement === "above";
  // pointer-events-auto: the portalled panel lives on <body>, and an open Radix
  // modal Dialog sets `pointer-events: none` there, so inside a dialog the
  // options could be seen but not clicked. The dialog lets clicks on
  // [data-dropdown-panel] through (ui/dialog.tsx).
  const position = cn(
    "pointer-events-auto z-[200] shadow-[0_8px_24px_rgba(0,0,0,0.12)]",
    "transition-[opacity,translate,scale] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)]",
    anchored
      ? "dropdown-panel-anchored"
      : cn("absolute top-full mt-1", align === "end" ? "right-0" : "left-0"),
    above ? "origin-bottom" : "origin-top",
    visible
      ? "translate-y-0 scale-y-100 opacity-100"
      : cn("scale-y-[0.96] opacity-0", above ? "translate-y-1.5" : "-translate-y-1.5"),
  );

  const node = glow ? (
    <GlowCard
      ref={panelRef ?? fallbackRef}
      data-dropdown-panel=""
      glow={glow}
      size="none"
      radius="md"
      {...(anchored && box ? { style: box.style } : {})}
      className={cn(position, className)}
    >
      {children}
    </GlowCard>
  ) : (
    <div
      ref={panelRef ?? fallbackRef}
      data-dropdown-panel=""
      {...(anchored && box ? { style: box.style } : {})}
      className={cn(position, "rounded-md border border-[var(--color-border)] bg-surface-0", className)}
    >
      {children}
    </div>
  );

  // Nothing is drawn until the trigger has been measured, so the panel never
  // flashes at the top-left corner on its first frame.
  if (!anchored) return node;
  return box ? createPortal(node, document.body) : null;
}

export interface DropdownOption {
  value: string;
  /** Usually a string. A node lets an option carry an icon beside its text
   *  (asset types); it is rendered in the trigger as well as in the list. */
  label: ReactNode;
}

interface DropdownSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder: string;
  className?: string;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  /** A dropdown whose value is always one of `options` (Period, Page size) passes
   *  false: the "" placeholder row is a trap there, since picking it would set an
   *  invalid value. Filters that mean "All X" keep it. */
  clearable?: boolean | undefined;
  /** The trigger's accessible name when the selected label alone isn't descriptive
   *  (e.g. "Month" on its own doesn't say "Period"). */
  ariaLabel?: string | undefined;
  disabled?: boolean | undefined;
  /** Classes for the floating option list (width, max height). */
  panelClassName?: string | undefined;
}

// A single-select dropdown built from DropdownButton + a floating option list, instead
// of a native <select>. Native selects auto-size their closed box to the WIDEST option
// (e.g. "NOTICE_PERIOD"), not the currently chosen one, which leaves a big gap between
// the visible text and the arrow. This sizes to only the selected label, every time.
// `open`/`onToggle`/`onClose` are controlled by the caller so a page can keep a single
// "which dropdown is open" state — opening one then closes any other automatically.
export function DropdownSelect({ value, onChange, options, placeholder, className, open, onToggle, onClose, clearable = true, ariaLabel, disabled, panelClassName }: DropdownSelectProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef(0);
  useCloseOnOutsideClick(containerRef, open, onClose, panelRef);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const selectedLabel = options.find(o => o.value === value)?.label ?? placeholder;

  const pick = (v: string) => {
    onChange(v);
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(onClose, CLOSE_DELAY_MS);
  };

  return (
    <div ref={containerRef} className="relative shrink-0">
      {/* The panel anchors to this wrapper rather than to the button, so it
          lines up with the trigger even inside a table cell or a modal. */}
      <div ref={triggerRef}>
        <DropdownButton expanded={open} onClick={onToggle} className={className} aria-label={ariaLabel} disabled={disabled}>
          {selectedLabel}
        </DropdownButton>
      </div>
      {open && (
        <DropdownPanel
          anchorRef={triggerRef}
          panelRef={panelRef}
          className={cn("min-w-full overflow-y-auto whitespace-nowrap", panelClassName)}
        >
          <MenuList role="listbox" className="p-1">
            {clearable && (
              <DropdownOptionRow active={value === ""} onClick={() => pick("")}>
                {placeholder}
              </DropdownOptionRow>
            )}
            {options.map(o => (
              <DropdownOptionRow key={o.value} active={value === o.value} onClick={() => pick(o.value)}>
                {o.label}
              </DropdownOptionRow>
            ))}
          </MenuList>
        </DropdownPanel>
      )}
    </div>
  );
}

// One option: no hover background of its own — the MenuList highlight glides to it.
function DropdownOptionRow({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      {...{ [MENU_ITEM_ATTR]: "" }}
      onClick={onClick}
      className={cn(
        "flex w-full cursor-pointer items-center gap-3 rounded-sm px-3 py-2 text-left text-[13px] text-surface-900 outline-none transition-colors duration-150",
        active && "font-semibold text-brand-700"
      )}
    >
      <span className="flex-1">{children}</span>
      {active ? <Check size={14} className="menu-check shrink-0" /> : <span className="w-3.5 shrink-0" />}
    </button>
  );
}

// A checkbox row for popover option lists (e.g. the Columns picker). Keeps native
// <label> + checkbox semantics so clicking the text toggles the box; place rows in a
// MenuList so the shared highlight glides between them.
export function DropdownCheckboxRow({ checked, onChange, disabled = false, children }: { checked: boolean; onChange: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <label
      {...{ [MENU_ITEM_ATTR]: "" }}
      className={cn(
        "flex select-none items-center gap-1.5 rounded-sm px-1.5 py-1 text-xs text-surface-900",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
      )}
    >
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} className="accent-brand-600" />
      {children}
    </label>
  );
}

interface DropdownMultiSelectProps {
  /** The applied selection; empty means "all". */
  values: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string; sub?: string | undefined }[];
  /** Trigger text when nothing is picked, e.g. "All Offices". */
  allLabel: string;
  className?: string | undefined;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  ariaLabel?: string | undefined;
}

/**
 * The filter-bar multi-select: a DropdownButton trigger opening a searchable checkbox
 * list (ui/checkbox-list, with "Select all") and Clear / Cancel / OK. Ticks are a draft
 * until OK; Cancel, an outside click or toggling the trigger discards them.
 * Controlled open state, like DropdownSelect, so a page keeps one "which is open".
 */
export function DropdownMultiSelect({ values, onChange, options, allLabel, className, open, onToggle, onClose, ariaLabel }: DropdownMultiSelectProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<Set<string>>(() => new Set(values));
  useCloseOnOutsideClick(containerRef, open, onClose, panelRef);

  // Every opening starts from what is applied.
  useEffect(() => { if (open) setDraft(new Set(values)); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = options.filter((o) => values.includes(o.value));
  const label = chosen.length === 0 ? allLabel : chosen.length === 1 ? chosen[0]!.label : `${chosen[0]!.label} +${chosen.length - 1}`;

  return (
    <div ref={containerRef} className="relative shrink-0">
      <div ref={triggerRef}>
        <DropdownButton expanded={open} onClick={onToggle} className={cn("max-w-[220px]", className)} aria-label={ariaLabel}>
          <span className="truncate">{label}</span>
        </DropdownButton>
      </div>
      {open && (
        <DropdownPanel anchorRef={triggerRef} panelRef={panelRef} className="w-64">
          <CheckboxList className="rounded-none border-0" options={options} selected={draft} onSelectedChange={setDraft} />
          <div className="flex items-center gap-2 border-t border-surface-100 p-2">
            <Button variant="ghost" size="sm" onClick={() => setDraft(new Set())} disabled={draft.size === 0}>Clear</Button>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={() => { onChange(options.filter((o) => draft.has(o.value)).map((o) => o.value)); onClose(); }}>OK</Button>
          </div>
        </DropdownPanel>
      )}
    </div>
  );
}
